import React from 'react';
import { Plus, CircleSlash } from 'lucide-react';
import { Alert, Button, TextInput } from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useMyPageStrings } from '../myPageStrings';
import { useDraft } from '../useDraft';
import {
  HERO_LISTS, heroExtrasDraft, heroExtrasPayload, heroExtrasInvalid, newItemId, saveStatus, showsHeroExtras,
} from '../MyPage.shared';
import { RowActions, SaveRow, moveItem, useFieldStyle } from '../parts/common';

// «کارت‌های معرفی» و «نکات برجسته» — the hero's two short lists (the
// landing-style templates draw them: «مدرن» puts the cards beside the portrait
// and the highlights in a strip under the hero, «کلاسیک» as ruled notes and a
// credentials bar). The doctor's own words; when a list is left empty the
// public page falls back to what it already states, and the hint says so.
//
// Both lists are ONE write — the hero section's content — so they share one
// «ذخیره», like the items of «خدمات».

const group = { border: `1px solid ${C.border}`, borderRadius: RADIUS.card, padding: '0.875rem', display: 'grid', gap: '0.75rem' };
const groupTitle = { fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary };
const rowBox = { display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' };

function ExtrasList({ listKey, items, onChange, readOnly, titleKey, hintKey, addKey }) {
  const s = useMyPageStrings();
  const { formatNumber } = useTranslation();
  const fieldStyle = useFieldStyle();
  const lim = HERO_LISTS[listKey];
  const patch = (idx, k, v) => onChange(items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  return (
    <div style={group} data-testid={`my-page-hero-${listKey}`}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
        <span style={{ ...groupTitle, flex: 1 }}>{s(titleKey)}</span>
        <span style={{ fontSize: '0.75rem', color: C.textSecondary }}>
          {s('myPage.items.count', { count: formatNumber(items.length), max: formatNumber(lim.max) })}
        </span>
      </div>
      <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>{s(hintKey)}</div>
      {items.map((it, i) => (
        <div key={it.id} style={rowBox} data-testid={`my-page-hero-${listKey}-${i}`}>
          <TextInput
            label={s('myPage.hero.extras.itemTitle')}
            value={it.title}
            onChange={(e) => patch(i, 'title', e.target.value)}
            maxLength={lim.titleMax}
            disabled={readOnly}
            error={!it.title.trim() ? s('myPage.hero.extras.titleRequired') : undefined}
            inputStyle={fieldStyle}
            style={{ flex: '1 1 9rem' }}
            data-testid={`my-page-hero-${listKey}-${i}-title`}
          />
          <TextInput
            label={s('myPage.hero.extras.itemText')}
            value={it.text}
            onChange={(e) => patch(i, 'text', e.target.value)}
            maxLength={lim.textMax}
            disabled={readOnly}
            inputStyle={fieldStyle}
            style={{ flex: '2 1 12rem' }}
            data-testid={`my-page-hero-${listKey}-${i}-text`}
          />
          <RowActions
            index={i} count={items.length} disabled={readOnly}
            onMove={(idx, delta) => onChange(moveItem(items, idx, delta))}
            onRemove={(idx) => onChange(items.filter((_, j) => j !== idx))}
            testId={`my-page-hero-${listKey}-${i}`}
          />
        </div>
      ))}
      {items.length < lim.max && !readOnly ? (
        <Button
          variant="outline" size="sm" className="justify-self-start"
          onClick={() => onChange([...items, { id: newItemId(), title: '', text: '' }])}
          data-testid={`my-page-hero-${listKey}-add`}
        >
          <Plus size={16} aria-hidden /> {s(addKey)}
        </Button>
      ) : null}
    </div>
  );
}

export default function HeroExtras({ page, section, actions, cards, readOnly }) {
  const s = useMyPageStrings();
  const { draft, setDraft, dirty, adopt, reset } = useDraft(heroExtrasDraft(section));
  const card = cards['hero-extras'] || {};
  const shown = showsHeroExtras(page.templateId);

  const save = async () => {
    const res = await actions.updateSection('hero-extras', 'hero', { content: heroExtrasPayload(draft) });
    if (res.ok) adopt(heroExtrasDraft(res.page?.sections?.find((x) => x.kind === 'hero')));
  };

  return (
    <div style={{ display: 'grid', gap: '0.875rem' }} data-testid="my-page-hero-extras">
      {shown ? (
        <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>{s('myPage.hero.extras.onlyModern')}</div>
      ) : (
        <Alert variant="neutral" icon={<CircleSlash size={16} aria-hidden />} data-testid="my-page-hero-extras-not-shown">
          {s('myPage.hero.extras.notShown')}
        </Alert>
      )}
      <ExtrasList
        listKey="highlights" items={draft.highlights} readOnly={readOnly}
        onChange={(highlights) => setDraft((d) => ({ ...d, highlights }))}
        titleKey="myPage.hero.extras.highlightsTitle" hintKey="myPage.hero.extras.highlightsHint" addKey="myPage.hero.extras.addHighlight"
      />
      <ExtrasList
        listKey="cards" items={draft.cards} readOnly={readOnly}
        onChange={(list) => setDraft((d) => ({ ...d, cards: list }))}
        titleKey="myPage.hero.extras.cardsTitle" hintKey="myPage.hero.extras.cardsHint" addKey="myPage.hero.extras.addCard"
      />
      {dirty || card.saving || card.error || card.savedAt ? (
        <SaveRow
          status={saveStatus({ dirty, saving: card.saving, error: card.error, savedAt: card.savedAt })}
          error={card.error}
          onSave={save}
          onReset={reset}
          disabled={readOnly || heroExtrasInvalid(draft)}
          testId="my-page-hero-extras"
        />
      ) : null}
    </div>
  );
}
